import React, { useState, useEffect, useCallback } from 'react';
import {
  getApiInteractions,
  getApiInteractionsSummary,
  isAdmin,
  ApiInteractionRow,
  ApiInteractionsSummary,
} from '../services/api';

// Admin-only API Interaction Tracking dashboard. Lists recent integration calls
// (netsuite / graph / messagemedia / claude / internal) with filtering by service
// and status, failures highlighted, and keyset "Load older" paging via the
// backend's returned next_before_id (mirrors the feed's paging idea). Admin gating
// here is UX-only — the backend enforces the ADMIN role — but is present per the
// engineering contract.

const PAGE_SIZE = 50;

// Service filter options. '' = all services. Mirrors the feed's TYPE_OPTIONS shape.
const SERVICE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All services' },
  { value: 'netsuite', label: 'NetSuite' },
  { value: 'graph', label: 'Graph' },
  { value: 'messagemedia', label: 'MessageMedia' },
  { value: 'claude', label: 'Claude' },
  { value: 'internal', label: 'Internal' },
];

// Status filter: '' = all, 'true' = ok, 'false' = failures only. String-valued so
// it maps cleanly onto a <select>; converted to the boolean the API expects.
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'true', label: 'OK only' },
  { value: 'false', label: 'Failures only' },
];

// Compact, locale-aware timestamp. Falls back to the raw string if unparseable.
const fmtTs = (ts: string): string => {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? ts : d.toLocaleString();
};

const fmtLatency = (ms: number | null): string =>
  ms == null ? '—' : ms >= 1000 ? (ms / 1000).toFixed(2) + 's' : ms + 'ms';

// Per-service pill color so the eye can scan by integration. Neutral default for
// anything the backend adds later that we don't yet have a color for.
const serviceClass = (service: string): string => {
  switch (service) {
    case 'netsuite': return 'bg-blue-100 text-blue-700';
    case 'graph': return 'bg-purple-100 text-purple-700';
    case 'messagemedia': return 'bg-teal-100 text-teal-700';
    case 'claude': return 'bg-amber-100 text-amber-700';
    case 'internal': return 'bg-gray-100 text-gray-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

// Compact relative time for the summary panel ("3m ago", "2h ago", "—" if null).
// Local to this file (no shared helper exists) and dependency-free.
const relativeTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 0) return 'just now';
  if (secs < 60) return secs + 's ago';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  const days = Math.floor(hrs / 24);
  if (days < 30) return days + 'd ago';
  return d.toLocaleDateString();
};

// Window selector options for the summary panel. Drives the days param on the
// summary fetch only — independent of the table's service/status filters.
const WINDOW_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Today' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
];

// Success-rate -> text color. Green >=99, amber 95-99, red <95. Shared by the KPI
// card and the per-service table so the eye reads health consistently.
const rateColor = (rate: number): string => {
  if (rate >= 99) return 'text-green-600';
  if (rate >= 95) return 'text-amber-600';
  return 'text-red-600';
};

// Short day label for the trend axis (e.g. "6/25"). Falls back to the raw string.
const fmtDayShort = (day: string): string => {
  const d = new Date(day);
  if (isNaN(d.getTime())) return day;
  return (d.getMonth() + 1) + '/' + d.getDate();
};

const ApiInteractions: React.FC = () => {
  const [rows, setRows] = useState<ApiInteractionRow[]>([]);
  const [service, setService] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Keyset cursor for the next page; null = no older rows remain.
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null);

  // SUMMARY PANEL state. The window selector drives ONLY the summary fetch (days
  // param) and is independent of the table's service/status filters. Summary
  // failures are isolated from the table — they render a small inline note and
  // never block the interactions list below.
  const [summaryDays, setSummaryDays] = useState(7);
  const [summary, setSummary] = useState<ApiInteractionsSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Build the typed params for getApiInteractions. ok is omitted entirely when the
  // status filter is "all" so the backend returns both passing and failing rows.
  const buildParams = useCallback(
    (beforeId?: number) => {
      const params: { limit: number; service?: string; ok?: boolean; before_id?: number } = {
        limit: PAGE_SIZE,
      };
      if (service) params.service = service;
      if (status !== '') params.ok = status === 'true';
      if (beforeId != null) params.before_id = beforeId;
      return params;
    },
    [service, status]
  );

  // Full (re)load: initial mount + any filter change. Replaces the list.
  const fullLoad = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const page = await getApiInteractions(buildParams());
      setRows(page.rows || []);
      setNextBeforeId(page.next_before_id ?? null);
    } catch (err) {
      setError('Failed to load API interactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // "Load older": fetch the next keyset page and append. Guarded by nextBeforeId.
  const loadOlder = useCallback(async () => {
    if (nextBeforeId == null) return;
    try {
      setLoadingMore(true);
      const page = await getApiInteractions(buildParams(nextBeforeId));
      setRows((prev) => [...prev, ...(page.rows || [])]);
      setNextBeforeId(page.next_before_id ?? null);
    } catch (err) {
      setError('Failed to load older interactions');
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, nextBeforeId]);

  // Reload whenever a filter changes (fullLoad's identity changes with buildParams).
  // Guarded so non-admins never even fire the request.
  useEffect(() => {
    if (!isAdmin()) return;
    fullLoad();
  }, [fullLoad]);

  // Summary fetch in its OWN effect keyed on the window selector — refetches on
  // window change. Isolated from the table load so a summary failure can't take
  // the interactions list down with it. Guarded so non-admins never fire it.
  useEffect(() => {
    if (!isAdmin()) return;
    let cancelled = false;
    (async () => {
      try {
        setSummaryLoading(true);
        setSummaryError(null);
        const data = await getApiInteractionsSummary(summaryDays);
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled) setSummaryError('Failed to load summary');
        console.error(err);
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [summaryDays]);

  // UX-only admin gate (backend enforces). Render an unauthorized note rather than
  // nothing so a mis-routed non-admin gets a clear, non-alarming explanation.
  if (!isAdmin()) {
    return (
      <div className="p-6 text-center text-gray-500">
        You don't have access to API interaction tracking. Ask an admin for the ADMIN role.
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Filter bar — mirrors the feed's sticky bar styling. */}
      <div className="bg-white px-3 sm:px-6 pt-3 sm:pt-4 sticky top-16 z-30 border-b border-gray-300">
        <div className="flex flex-wrap items-end gap-2 sm:gap-3 pb-3">
          <div className="mr-auto">
            <h2 className="text-lg font-bold text-gray-900">API Interactions</h2>
            <p className="text-xs text-gray-500">Outbound/inbound integration calls (admin-only)</p>
          </div>

          <select
            value={service}
            onChange={(e) => setService(e.target.value)}
            title="Filter by service"
            className={'flex-shrink-0 px-2 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-brand-500 ' +
              (service ? 'border-brand-500 text-brand-700 bg-brand-50 font-medium' : 'border-gray-300 text-gray-700 bg-white')}
          >
            {SERVICE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            title="Filter by status"
            className={'flex-shrink-0 px-2 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-brand-500 ' +
              (status !== '' ? 'border-brand-500 text-brand-700 bg-brand-50 font-medium' : 'border-gray-300 text-gray-700 bg-white')}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <button
            onClick={() => fullLoad()}
            className="flex-shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-auto p-3 sm:p-6 bg-gray-50">
        <div className="w-full max-w-[1800px] mx-auto">
          {/* ===== SUMMARY PANEL (business-indicator) — admin-gated with the rest
               of the component; window selector drives only this panel. ===== */}
          <div className="mb-6">
            {/* Header row + window selector (segmented control) */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <h3 className="text-base font-bold text-gray-900">Health summary</h3>
                <p className="text-xs text-gray-500">
                  Last {summaryDays === 1 ? 'day' : summaryDays + ' days'} across all integrations
                </p>
              </div>
              <div className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden" role="group" aria-label="Summary window">
                {WINDOW_OPTIONS.map((o, i) => (
                  <button
                    key={o.value}
                    onClick={() => setSummaryDays(o.value)}
                    className={
                      'px-3 py-1.5 text-sm font-medium transition-colors ' +
                      (i > 0 ? 'border-l border-gray-300 ' : '') +
                      (summaryDays === o.value
                        ? 'bg-brand-600 text-white'
                        : 'text-gray-700 hover:bg-gray-50')
                    }
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary loading — small inline spinner, never blocks the table */}
            {summaryLoading && !summary && (
              <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-700"></div>
                Loading summary…
              </div>
            )}

            {/* Summary error — inline note ONLY; the table below is unaffected */}
            {summaryError && !summary && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                {summaryError}. The interactions table below is unaffected.
              </div>
            )}

            {summary && (
              <>
                {/* KPI cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  {/* (a) Total interactions */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Total interactions</div>
                    <div className="text-3xl font-bold text-gray-900 mt-1">
                      {summary.totals.total.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {summary.totals.inbound.toLocaleString()} in / {summary.totals.outbound.toLocaleString()} out
                    </div>
                  </div>

                  {/* (b) Success rate — color-coded */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Success rate</div>
                    <div className={'text-3xl font-bold mt-1 ' + rateColor(summary.totals.success_rate)}>
                      {summary.totals.success_rate.toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {summary.totals.ok.toLocaleString()} ok of {summary.totals.total.toLocaleString()}
                    </div>
                  </div>

                  {/* (c) Failures — red-tinted if any */}
                  <div className={'rounded-lg border p-4 ' + (summary.totals.failed > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200')}>
                    <div className="text-xs font-medium text-gray-500 uppercase">Failures</div>
                    <div className={'text-3xl font-bold mt-1 ' + (summary.totals.failed > 0 ? 'text-red-600' : 'text-gray-900')}>
                      {summary.totals.failed.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {summary.totals.failed > 0 ? 'in the last ' + (summaryDays === 1 ? 'day' : summaryDays + ' days') : 'none — all clear'}
                    </div>
                  </div>

                  {/* (d) Avg latency — volume-weighted mean of by_service avgs */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Avg latency</div>
                    {(() => {
                      // Weight each service's avg by its count; ignore null avgs so a
                      // service with no latency data doesn't poison the mean.
                      let num = 0;
                      let den = 0;
                      summary.by_service.forEach((s) => {
                        if (s.avg_latency_ms != null && s.count > 0) {
                          num += s.avg_latency_ms * s.count;
                          den += s.count;
                        }
                      });
                      const avg = den > 0 ? num / den : null;
                      return (
                        <div className="text-3xl font-bold text-gray-900 mt-1">
                          {avg == null ? '—' : fmtLatency(Math.round(avg))}
                        </div>
                      );
                    })()}
                    <div className="text-xs text-gray-500 mt-1">volume-weighted, all services</div>
                  </div>
                </div>

                {/* Per-service health table */}
                {summary.by_service.length > 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                        <tr>
                          <th className="text-left font-medium px-3 py-2">Service</th>
                          <th className="text-right font-medium px-3 py-2">Count</th>
                          <th className="text-right font-medium px-3 py-2">Success</th>
                          <th className="text-right font-medium px-3 py-2">Failed</th>
                          <th className="text-right font-medium px-3 py-2">Avg latency</th>
                          <th className="text-right font-medium px-3 py-2">Max latency</th>
                          <th className="text-left font-medium px-3 py-2">Last failure</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {summary.by_service.map((s) => (
                          <tr key={s.service} className={s.failed > 0 ? 'bg-red-50' : ''}>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={'px-2 py-0.5 rounded-full text-xs font-medium ' + serviceClass(s.service)}>
                                {s.service}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">{s.count.toLocaleString()}</td>
                            <td className={'px-3 py-2 whitespace-nowrap text-right font-medium ' + rateColor(s.success_rate)}>
                              {s.success_rate.toFixed(1)}%
                            </td>
                            <td className={'px-3 py-2 whitespace-nowrap text-right ' + (s.failed > 0 ? 'text-red-600 font-medium' : 'text-gray-500')}>
                              {s.failed.toLocaleString()}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">{fmtLatency(s.avg_latency_ms)}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">{fmtLatency(s.max_latency_ms)}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-500" title={s.last_failure_ts || undefined}>
                              {relativeTime(s.last_failure_ts)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 mb-4">No service activity in this window.</div>
                )}

                {/* Trend — compact CSS mini bar chart; failed portion tinted red */}
                {summary.by_day.length > 0 && (() => {
                  const maxCount = Math.max(1, ...summary.by_day.map((d) => d.count));
                  return (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="text-xs font-medium text-gray-500 uppercase mb-2">
                        Volume trend ({summary.by_day.length}d)
                      </div>
                      <div className="flex items-end gap-1 h-24">
                        {summary.by_day.map((d) => {
                          const totalH = Math.round((d.count / maxCount) * 100);
                          const failH = d.count > 0 ? Math.round((d.failed / d.count) * totalH) : 0;
                          return (
                            <div
                              key={d.day}
                              className="flex-1 flex flex-col justify-end h-full min-w-[2px]"
                              title={`${fmtDayShort(d.day)}: ${d.count} calls, ${d.failed} failed`}
                            >
                              {/* failed portion (red) stacked atop the ok portion (brand) */}
                              <div className="w-full bg-red-400 rounded-t-sm" style={{ height: failH + '%' }}></div>
                              <div className="w-full bg-brand-400" style={{ height: (totalH - failH) + '%' }}></div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Sparse axis: first + last day */}
                      <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                        <span>{fmtDayShort(summary.by_day[0].day)}</span>
                        <span>{fmtDayShort(summary.by_day[summary.by_day.length - 1].day)}</span>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>

          {loading && rows.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-700"></div>
            </div>
          )}

          {error && rows.length === 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No API interactions found
            </div>
          )}

          {rows.length > 0 && (
            <>
              {/* A non-blocking error banner once rows already exist (e.g. a failed
                  "load older") so the table stays visible. */}
              {error && (
                <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">Time</th>
                      <th className="text-left font-medium px-3 py-2">Service</th>
                      <th className="text-left font-medium px-3 py-2">Dir</th>
                      <th className="text-left font-medium px-3 py-2">Method</th>
                      <th className="text-left font-medium px-3 py-2">Endpoint</th>
                      <th className="text-left font-medium px-3 py-2">Status</th>
                      <th className="text-right font-medium px-3 py-2">Latency</th>
                      <th className="text-left font-medium px-3 py-2">Principal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        className={r.ok ? 'hover:bg-gray-50' : 'bg-red-50 hover:bg-red-100'}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{fmtTs(r.ts)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={'px-2 py-0.5 rounded-full text-xs font-medium ' + serviceClass(r.service)}>
                            {r.service}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-500">{r.direction}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-mono text-gray-700">{r.method || '—'}</td>
                        <td className="px-3 py-2 font-mono text-gray-700 max-w-md truncate" title={r.endpoint || undefined}>
                          {r.endpoint || '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span
                            className={'px-2 py-0.5 rounded-full text-xs font-medium ' +
                              (r.ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}
                            title={r.error || undefined}
                          >
                            {r.status_code ?? (r.ok ? 'OK' : 'ERR')}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">{fmtLatency(r.latency_ms)}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 max-w-[180px] truncate" title={r.principal || undefined}>
                          {r.principal || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Keyset "Load older" — shown only while the backend reports more rows. */}
              {nextBeforeId != null && (
                <div className="flex justify-center py-4">
                  <button
                    onClick={() => loadOlder()}
                    disabled={loadingMore}
                    className="px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loadingMore ? 'Loading…' : 'Load older'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiInteractions;
